import { useCallback, useEffect, useRef, useState } from 'react';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { getLocalCalendarDayString, logTermsAcceptance, readTermsAcceptanceDailyCache, writeTermsAcceptanceDailyCache, } from '@ibk/shared-utils';
import { ACCEPT_PLATFORM_TERMS_KIND } from '../Onboarding/services/acceptPlatformTermsKind';
import { useAcceptPlatformTermsMutation, useLazyGetTermsAcceptStatusQuery } from '../Onboarding/services/onboardingApi';
import { startFreshTermsStatusEffect, startRestorableDailyCacheEffect } from './termsAcceptanceGateEffectRunners';
import { closeGateAndSetReady, dailyCacheMatchesUserAndDay, hasRestorablePendingSnapshot, persistSessionUiAck, readSessionUiAck, } from './termsAcceptanceGateUtils';
export function useTermsAcceptanceGate({ enabled, userId, deferPendingUntilUserId = false, }) {
    const { isFeatureFlagWhiteListed, getFeatureFlags } = useFeatureFlag(userId, {
        isLoggedIn: true,
        autoLoad: false,
    });
    const userIdRef = useRef(userId);
    userIdRef.current = userId;
    const isFeatureFlagWhiteListedRef = useRef(isFeatureFlagWhiteListed);
    isFeatureFlagWhiteListedRef.current = isFeatureFlagWhiteListed;
    const getFeatureFlagsRef = useRef(getFeatureFlags);
    getFeatureFlagsRef.current = getFeatureFlags;
    const [triggerStatus] = useLazyGetTermsAcceptStatusQuery();
    const triggerStatusRef = useRef(triggerStatus);
    triggerStatusRef.current = triggerStatus;
    const [acceptTerms] = useAcceptPlatformTermsMutation();
    const [modalOpen, setModalOpen] = useState(false);
    const [gateReady, setGateReady] = useState(false);
    const [pendingTerms, setPendingTerms] = useState(null);
    useEffect(() => {
        const setters = { setModalOpen, setPendingTerms, setGateReady };
        if (!enabled) {
            closeGateAndSetReady(setters);
            return;
        }
        if (readSessionUiAck()) {
            closeGateAndSetReady(setters);
            return;
        }
        if (deferPendingUntilUserId && (userId == null || userId === '')) {
            closeGateAndSetReady(setters);
            return;
        }
        const today = getLocalCalendarDayString();
        const dailyCache = readTermsAcceptanceDailyCache();
        if (dailyCache != null && dailyCacheMatchesUserAndDay(dailyCache, today, userId)) {
            if (dailyCache.serverAccepted) {
                closeGateAndSetReady(setters);
                return;
            }
            if (hasRestorablePendingSnapshot(dailyCache.pendingSnapshot)) {
                return startRestorableDailyCacheEffect(dailyCache, userIdRef, getFeatureFlagsRef, isFeatureFlagWhiteListedRef, setters);
            }
        }
        return startFreshTermsStatusEffect(userIdRef, getFeatureFlagsRef, isFeatureFlagWhiteListedRef, triggerStatusRef, setters);
    }, [enabled, userId, deferPendingUntilUserId]);
    const termId = pendingTerms?.term?.termId;
    const termVersionId = pendingTerms?.term?.termVersionId;
    const handleAccept = useCallback(async () => {
        if (!termId || !termVersionId) {
            logTermsAcceptance('gate.accept_blocked_missing_ids', { description: 'Confirmação sem termId/termVersionId; aceite não enviado ao servidor.' }, 'error');
            throw new Error('Missing termId/termVersionId for platform terms acceptance.');
        }
        try {
            await acceptTerms({
                kind: ACCEPT_PLATFORM_TERMS_KIND.TERM_VERSION,
                termId,
                termVersionId,
            }).unwrap();
            logTermsAcceptance('gate.accept_success', {
                description: 'Aceite persistido; cache diário e sessão atualizados; modal fechado.',
            });
            writeTermsAcceptanceDailyCache({
                day: getLocalCalendarDayString(),
                serverAccepted: true,
                ...(userId != null && userId !== '' ? { userId } : {}),
            });
            persistSessionUiAck();
            setModalOpen(false);
            setPendingTerms(null);
        }
        catch (cause) {
            logTermsAcceptance('gate.accept_api_error', { description: 'POST platform-terms/accept falhou; modal fechado sem persistir aceite.' }, 'error', cause);
            setModalOpen(false);
            setPendingTerms(null);
        }
    }, [acceptTerms, termId, termVersionId, userId]);
    return {
        gateReady,
        modalOpen,
        presentation: pendingTerms?.presentation,
        handleAccept,
    };
}
